"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function BlogListConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className={cn("flex flex-col gap-4", i % 2 !== 0 ? "md:mt-[60px]" : "")}>
            <div className="w-full aspect-[4/3] bg-muted/10 animate-pulse rounded-md" />
            <div className="h-6 w-24 bg-muted/10 animate-pulse rounded-lg mt-4" />
            <div className="h-8 w-3/4 bg-muted/10 animate-pulse rounded mt-2" />
            <div className="h-20 w-full bg-muted/10 animate-pulse rounded mt-2" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load intelligence reports.</p>
        <Button variant="outline" onClick={async () => await loadMore()}>Retry Connection</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center text-muted-foreground py-12 font-default">
        No intelligence data found. System standby.
      </div>;
  }
  return <div className="flex flex-col items-center">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 w-full">
        {posts.map((post, i) => {
        const category = post.categories?.[0]?.name || "Tech Intelligence";
        const date = post.date ? new Date(post.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }) : "Live";
        return <article key={post.id} data-index={i} className={cn("group flex flex-col h-full", i % 2 !== 0 ? "md:mt-[60px]" : "")}>
              <Link to={post.link || "#"} className="block overflow-hidden rounded-md mb-6 border border-white/10">
                <AspectRatio ratio={4 / 3} className="bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110 saturate-150" /> : <div className="w-full h-full flex items-center justify-center bg-muted/20 text-muted-foreground font-mono text-xs">
                      [IMAGE_NOT_FOUND]
                    </div>}
                </AspectRatio>
              </Link>

              <div className="flex flex-col flex-grow">
                <div className="mb-4">
                  <Badge variant="outline" className="text-primary border-primary/50 uppercase tracking-widest text-[10px] font-mono px-3 py-1 rounded-md bg-primary/5">
                    {category}
                  </Badge>
                </div>

                <Link to={post.link || "#"} className="block group-hover:text-primary transition-colors duration-300">
                  <h3 className="font-default text-2xl md:text-3xl font-bold leading-tight mb-4 tracking-tight" dangerouslySetInnerHTML={{
                __html: post.title
              }} />
                </Link>

                <div className="font-default text-muted-foreground line-clamp-3 mb-8 text-base leading-relaxed" dangerouslySetInnerHTML={{
              __html: post.excerpt || "Analyzing the latest shifts in cybersecurity, AI automation, and enterprise software development."
            }} />

                <div className="mt-auto flex items-center justify-between pt-6 border-t border-white/10">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono uppercase tracking-tighter">
                    <span>{date}</span>
                    <span className="w-1 h-1 rounded-md bg-primary" />
                    <span>System Log</span>
                  </div>

                  <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-foreground group-hover:text-primary transition-all">
                    Read More
                    <span className="transition-transform duration-300 group-hover:translate-x-2">→</span>
                  </Link>
                </div>
              </div>
            </article>;
      })}
      </div>

      {hasNext && <div className="mt-24">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="font-mono uppercase tracking-widest font-bold border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md px-10 h-14 shadow-lg shadow-primary/10">
            {isRefetching ? "Syncing..." : "Load More Intelligence"}
          </Button>
        </div>}
    </div>;
}
export default function TechIntelligence() {
  const wp_query_params = {
    post_type: "post",
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="236476" id="tech-intelligence" className="py-24 md:py-32 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="mb-16">
          <h2 className="font-default text-4xl md:text-5xl font-bold mb-4 tracking-tighter">Tech Intelligence</h2>
          <p className="font-default text-muted-foreground max-w-2xl">Strategic insights into the future of IT solutions, cybersecurity, and AI automation.</p>
        </div>
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        order: "desc",
        orderby: "date"
      }}>
          <BlogListConsumer />
        </PostsProvider>
      </div>
    </section>;
}